import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { priceOf } from "@/config";
import { useCustomer } from "./CustomerContext";
import { cartKey, emptyCart, persistCart, readCart, normalizePhone } from "@/lib/cartStorage";
import { useCartSync } from "@/hooks/useCartSync";
import { itemConfigurationError } from "@/lib/productDetails";

const CartContext = createContext(null);

function CustomerCart({ children, phone }) {
  const { ensureCartSession } = useCustomer();
  const [cart, setCart] = useState(() => readCart(phone) || emptyCart());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const current = useRef(cart);
  current.current = cart;

  const save = useCallback((next) => {
    if (JSON.stringify(current.current) === JSON.stringify(next)) return;
    current.current = next;
    persistCart(phone, next);
    setCart(next);
  }, [phone]);

  const mutate = useCallback((transform) => {
    const stored = readCart(phone);
    const base = stored?.dirty && stored.editedAt > (current.current.editedAt || 0) ? stored : current.current;
    const editedAt = Math.max(Date.now(), (base.editedAt || 0) + 1);
    save({ ...base, items: transform(base.items), dirty: true, changeId: crypto.randomUUID(), editedAt,
      revision: Math.max(editedAt, base.revision + 1, (base.baseRevision || 0) + 1) });
  }, [phone, save]);

  const resetSubmitted = useCallback((cartId) => {
    if (current.current.cartId !== cartId) return;
    save({ ...emptyCart(), editedAt: Math.max(Date.now(), (current.current.editedAt || 0) + 1) });
  }, [save]);

  const { syncStatus, cartReady, retrySync } = useCartSync({ phone, cart, cartRef: current, ensureCartSession, save });

  useEffect(() => { if (drawerOpen) retrySync(); }, [drawerOpen, retrySync]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== cartKey(phone)) return;
      const saved = readCart(phone);
      if (saved && ((!current.current.dirty && saved.revision >= current.current.revision) ||
          (saved.dirty && saved.editedAt > (current.current.editedAt || 0)))) {
        current.current = saved;
        setCart(saved);
      }
      retrySync();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [phone, retrySync]);

  const addItem = (product, qty = 1, { silent = false, size_quantities = {}, details } = {}) => {
    mutate((items) => {
      const found = items.find((item) => item.product.id === product.id);
      const sizes = { ...(found?.size_quantities || {}) };
      Object.entries(size_quantities).forEach(([size, count]) => { sizes[size] = (sizes[size] || 0) + count; });
      const next = { product, qty: (found?.qty || 0) + qty, size_quantities: sizes, details: details === undefined ? (found?.details || "") : details.trim() };
      return found ? items.map((item) => item.product.id === product.id ? next : item) : [...items, next];
    });
    if (!silent) toast.success(`${product.name} added to order`);
  };
  const updateQty = (productId, qty) => mutate((items) => qty <= 0 ? items.filter((item) => item.product.id !== productId) : items.map((item) => item.product.id === productId ? { ...item, qty } : item));
  const removeItem = (productId) => mutate((items) => items.filter((item) => item.product.id !== productId));
  const updateItemDetails = (productId, values) => mutate((items) => items.map((item) => item.product.id === productId ? { ...item, ...values } : item));
  const clear = ({ submitted = false } = {}) => submitted ? resetSubmitted(current.current.cartId) : mutate(() => []);
  const checkoutTracking = async () => {
    const success = await retrySync();
    if (!success) throw new Error("Your cart is still syncing. Please retry before placing the order.");
    if (!current.current.items.length) throw new Error("Your latest cart is empty.");
    if (current.current.items.some((item) => item.product.unavailable)) throw new Error("Remove unavailable products before placing the order.");
    const incomplete = current.current.items.find((item) => itemConfigurationError(item));
    if (incomplete) throw new Error(`${incomplete.product.name}: ${itemConfigurationError(incomplete)}`);
    const session = await ensureCartSession();
    return { cart_id: current.current.cartId, items: current.current.items,
      total: current.current.items.reduce((sum, item) => sum + priceOf(item.product) * item.qty, 0),
      headers: { Authorization: `Bearer ${session.cart_token}` } };
  };
  const count = cart.items.reduce((sum, item) => sum + item.qty, 0);
  const total = cart.items.reduce((sum, item) => sum + item.qty * priceOf(item.product), 0);

  return <CartContext.Provider value={{ items: cart.items, count, total, addItem, updateQty, removeItem, updateItemDetails, clear, drawerOpen, setDrawerOpen, syncStatus, cartReady, retrySync, checkoutTracking }}>{children}</CartContext.Provider>;
}

export function CartProvider({ children }) {
  const { customer } = useCustomer();
  const phone = normalizePhone(customer?.phone);
  // A different customer never inherits another customer's cart, in-memory or on disk.
  return <CustomerCart key={phone || "guest"} phone={phone}>{children}</CustomerCart>;
}

export const useCart = () => useContext(CartContext);